import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLink,
  faTriangleExclamation,
  faMagnifyingGlassChart,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import styles from './VerdeScoreSection.module.css';

const capabilities = [
  {
    icon: faLink,
    title: 'Evidence rows',
    text: 'Each material conclusion can point back to the document, note, or assumption that supports it.'
  },
  {
    icon: faTriangleExclamation,
    title: 'Open diligence',
    text: 'Known facts, missing inputs, provisional assumptions, and conflicting signals stay visible.'
  },
  {
    icon: faMagnifyingGlassChart,
    title: 'Internal risk review',
    text: 'The framework helps reviewers compare matters without issuing external investment ratings.'
  },
  {
    icon: faUserCheck,
    title: 'Analyst acceptance',
    text: 'AI-assisted draft material becomes report content only after a reviewer accepts, edits, or rejects it.'
  }
];

const signals = [
  {
    color: 'green',
    label: 'Supported',
    description: 'Source-backed finding with limited follow-up'
  },
  {
    color: 'yellow',
    label: 'Needs diligence',
    description: 'Mixed support or unresolved assumption'
  },
  {
    color: 'red',
    label: 'Challenge',
    description: 'Adverse signal, contradiction, or missing support'
  }
];

const VerdeScoreSection = () => {
  return (
    <section id="verdescore-section" className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.badge}>Review Artifacts</span>
          <h2 className={styles.title}>Artifacts for comparing, challenging, and defending judgment.</h2>
          <p className={styles.subtitle}>
            VerdeKai supports internal screening and diligence. It does not issue external
            investment ratings or recommendations.
          </p>
        </div>

        <div className={styles.contentGrid}>
          <div className={styles.capabilityList}>
            {capabilities.map((capability) => (
              <div key={capability.title} className={styles.capabilityRow}>
                <div className={styles.capabilityIconBox}>
                  <FontAwesomeIcon icon={capability.icon} className={styles.capabilityIcon} />
                </div>
                <div className={styles.capabilityText}>
                  <h3 className={styles.capabilityTitle}>{capability.title}</h3>
                  <p className={styles.capabilityDesc}>{capability.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.packetCard} aria-label="Committee packet artifact preview">
            <div className={styles.packetHeader}>
              <span className={styles.packetEyebrow}>Committee Packet</span>
              <span className={styles.packetStage}>Draft</span>
            </div>
            <h3 className={styles.packetTitle}>Review record</h3>
            <div className={styles.packetBlock}>
              <span className={styles.packetLabel}>Assumption register</span>
              <div className={styles.packetLine}>
                <span>Base damages</span>
                <strong>Needs diligence</strong>
              </div>
              <div className={styles.packetLine}>
                <span>Duration sensitivity</span>
                <strong>Supported</strong>
              </div>
            </div>
            <div className={styles.packetBlock}>
              <span className={styles.packetLabel}>Evidence-linked output</span>
              <div className={styles.evidenceStack}>
                <span>Risk posture summary</span>
                <span>Adversarial challenge memo</span>
                <span>Diligence agenda</span>
                <span>Editable IC narrative</span>
              </div>
            </div>
          </div>
        </div>

        <p className={styles.capabilityNote}>VerdeKai prepares the record. The committee owns the decision.</p>

        <div className={styles.explainabilityStrip}>
          <h4 className={styles.stripTitle}>Internal classification, not external ratings.</h4>
          <div className={styles.signalGrid}>
            {signals.map((signal, index) => (
              <div key={index} className={styles.signalCard}>
                <span className={`${styles.signalDot} ${styles[signal.color]}`}></span>
                <div className={styles.signalText}>
                  <span className={styles.signalLabel}>{signal.label}</span>
                  <span className={styles.signalDesc}>{signal.description}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VerdeScoreSection;
